import React, { useState, useEffect, useCallback } from 'react';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { deleteGroup } from '@/repository/OrganizationDAO';
import ConfirmModal from '@/components/modals/utils/ConfirmModal';
import CreateGroupModal from './modals/CreateGroupModal';
import { useUserUpdaterProvider } from '@/context_providers/user_context/UserUpdaterProvider';
import InfoModal from '@/components/modals/utils/InfoModal';
import { useHistory } from 'react-router-dom';
import EditGroupNameModal from './modals/EditGroupNameModal';
import LoadingData from '@/components/loading/LoadingData';
import Loading from '@/components/loading/Loading';
import { useToast } from '@/context_providers/toast_context/ToastContext';
import { deleteUserViaAdmin } from '@/repository/UserDAO';
import LoadingSpinner from '@/components/loading/LoadingSpinner';
import GroupManagementRouteBar from './GroupManagementRouteBar';
import GroupRoute from './GroupRoute';
import UsersInAGroup from './UsersInAGroup';
import UserTable from './UserTable';
import { useTranslation } from 'react-i18next';
import {
  ErrorStringKeys,
  StringKeys,
  SuccessStringKeys,
} from '@/resources/strings/StringKeys';
import { CssBaseline } from '@mui/material';
import ErrorComponent from '@/components/utils/ErrorComponent';
import APIErrorCodes from '@/utils/error_codes/APIErrorCodes';
import { useOrganizationUpdaterProvider } from '@/context_providers/organization_context/OrganizationProvider';
import { OrgUser } from '@/models/User/OrgUser';
import Group from '@/models/Organization/Group';
import GroupView from './GroupView';
import { getPoolRemaining } from '@/utils/organization/orgSeatAvailability';
import { SubscriptionTier } from '@/../generated-client/models';
import { flattenAllOrgUsers } from '@/utils/organization/allUsersOverview';
import { ALL_USERS_SLUG, isAllUsersPath } from './allUsersConstants';
import { Routes } from '@/enums/Routes';

export interface GroupData {
  paidTierUsers: OrgUser[];
  connectTierUsers: OrgUser[];
  paidTierSeatsRemaining: number;
}

type UsersByGroup = Record<string, GroupData>;

const GroupManagement = () => {
  const [loading, setLoading] = useState(false);
  const [usersByGroup, setUsersByGroup] = useState<UsersByGroup>({});
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmTitle, setConfirmTitle] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<OrgUser>();
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [changingGroupUserGroup, setChangingGroupUserGroup] = useState();
  const [showCreateNewGroupModal, setShowCreateNewGroupModal] = useState(false);
  const [loadingByGroup, setLoadingByGroup] = useState({});
  const [userToHighlight, setUserToHighlight] = useState<string | null>(null);
  const [activeGroup, setActiveGroup] = useState(ALL_USERS_SLUG);
  const { orgUser } = useUserUpdaterProvider();
  const [infoMessage, setInfoMessage] = useState('');
  const [infoTitle, setInfoTitle] = useState('');
  const [showInfoModal, setShowInfoModal] = useState(false);
  const history = useHistory();
  const [showEditGroupNameModal, setShowEditGroupNameModal] = useState(false);
  const [loadingAPIData, setLoadingAPIData] = useState(false);

  const [fetchError, setFetchError] = useState(false);
  const { showErrorToast, showSuccessToast } = useToast();
  const { groups, organization } = useOrganizationUpdaterProvider();
  const { t } = useTranslation();
  const [allGroups, setAllGroups] = useState<Group[]>([]);

  useEffect(() => {
    if (userToHighlight != null) {
      setTimeout(() => {
        setUserToHighlight(null);
      }, 2000);
    }
  }, [userToHighlight]);

  useEffect(() => {
    if (groups) setAllGroups(groups);
  }, [groups]);

  useEffect(() => {
    if (
      history.location.pathname === Routes.USER_MANAGEMENT ||
      history.location.pathname === `${Routes.USER_MANAGEMENT}/`
    ) {
      history.replace(Routes.USER_MANAGEMENT_ALL_USERS);
    }
  }, [history]);

  useEffect(() => {
    const path = history.location.pathname;
    if (isAllUsersPath(path)) {
      setActiveGroup(ALL_USERS_SLUG);
      return;
    }
    const prefix = `${Routes.USER_MANAGEMENT}/`;
    if (path.startsWith(prefix)) {
      setActiveGroup(decodeURIComponent(path.slice(prefix.length)));
    }
  }, [history.location.pathname]);

  /**
   * Renders the users in a specific group.
   * @param {string} groupName
   * @returns
   */
  const renderUsersInAGroup = useCallback(
    (groupName: string) => {
      const usersInAGroup = usersByGroup[groupName];
      return (
        <UsersInAGroup
          usersInAGroup={usersInAGroup}
          groupName={groupName}
          renderUsers={renderUsers}
        />
      );
    },
    [usersByGroup],
  );

  // MARK: ORG CHECK
  /**
   * If the org does not exist, we don't want to render anything!
   */
  if (!organization) return;

  const deleteGroupLocal = async (groupName: string) => {
    setLoadingAPIData(true);
    try {
      if (!organization?.org_code) {
        showErrorToast(
          t(ErrorStringKeys.ORGANIZATION_DOES_NOT_EXIST_TRY_REFRESHING),
        );
        console.error('tried deleting an org that does not exist');
        return;
      }
      await deleteGroup(groupName, organization?.org_code, organization?.id);

      showSuccessToast(
        t(SuccessStringKeys.SUCCESSFULLY_REMOVED_GROUP, {
          group: `${groupName}`,
        }),
      );

      history.replace(`/UserManagement/Default`);
    } catch (error) {
      showErrorToast(
        t(ErrorStringKeys.FAILED_TO_REMOVE_GROUP, { group: `${groupName}` }),
      );
    } finally {
      setLoadingAPIData(false);
    }
  };

  const removeUser = async () => {
    const orgUserAzureId = selectedUser?.org_user_azure_id || selectedUser?.id;
    if (!orgUserAzureId) {
      console.error('The user does not have an org_user_azure_id');
      showErrorToast(
        t(ErrorStringKeys.USER_DOES_NOT_SEEM_TO_EXIST_TRY_REFRESHING),
      );
      return;
    }

    setLoadingAPIData(true);
    setShowConfirmModal(false);
    if (!selectedUser || !organization) {
      return;
    }

    try {
      await deleteUserViaAdmin(
        organization.org_code,
        orgUserAzureId,
        organization.id,
      );

      showSuccessToast(
        t(SuccessStringKeys.SUCCESSFULLY_REMOVED_USER_FROM_ORGANIZATION, {
          user: `${selectedUser.first_name} ${selectedUser.last_name}`,
          organization: `${organization.company_name}`,
        }),
      );
    } catch (error) {
      showErrorToast(
        t(ErrorStringKeys.FAILED_TO_REMOVE_USER_FROM_ORGANIZATION, {
          user: `${selectedUser.first_name} ${selectedUser.last_name}`,
          organization: `${organization.company_name}`,
        }),
      );
    } finally {
      setLoadingAPIData(false);
    }
  };

  const renderGroup = (
    group_name: string,
    group_value: OrgUser[],
    paidTierUserAuthIds: string[],
    connectTierUserAuthIds: string[],
  ) => {
    return (
      <GroupView
        group_name={group_name}
        group_value={group_value}
        paidTierUserAuthIds={paidTierUserAuthIds}
        connectTierUserAuthIds={connectTierUserAuthIds}
        fetchGroupContacts={fetchGroupContacts}
        renderUsersInAGroup={renderUsersInAGroup}
      />
    );
  };

  const fetchGroupContacts = async (
    groupName: string,
    groupValue: OrgUser[],
    paidTierUserAuthIds: string[],
    connectTierUserAuthIds: string[],
  ) => {
    try {
      setLoadingByGroup({ ...loadingByGroup, [groupName]: true });
      setLoading(true);
      setFetchError(false);
      const subbedUsers = groupValue.filter((user) =>
        paidTierUserAuthIds.includes(user.auth_id),
      );
      const unsubbedUsers = groupValue.filter((user) =>
        connectTierUserAuthIds.includes(user.auth_id),
      );
      const subUserLists = {
        paidTierUsers: subbedUsers,
        connectTierUsers: unsubbedUsers,
        paidTierSeatsRemaining:
          getPoolRemaining(organization, SubscriptionTier.Ops) +
          getPoolRemaining(organization, SubscriptionTier.Respond),
      };
      setUsersByGroup({ ...usersByGroup, [groupName]: subUserLists });
      setLoading(false);
      setExpandedGroup(expandedGroup === groupName ? null : groupName);
      setLoadingByGroup({ ...loadingByGroup, [groupName]: false });
    } catch (error) {
      setLoadingByGroup({ ...loadingByGroup, [groupName]: false });
      setLoading(false);
      setFetchError(true);
    }
  };

  /**
   * Navigates to the edited group after a group name change.
   * @param {string} groupName
   */
  const navigateToEditedGroup = (groupName: string) => {
    const updatedGroups = [...allGroups];
    const index = updatedGroups.findIndex(
      (group) => group.group_name === activeGroup,
    );
    if (index !== -1) updatedGroups[index].group_name = groupName;
    setActiveGroup(groupName);
    setAllGroups(updatedGroups);
    history.replace(`/UserManagement/${encodeURIComponent(groupName)}`);
    renderRoutes();
  };

  const renderRoutes = () => {
    return (
      <>
        <CssBaseline />
        <GroupManagementRouteBar
          allGroups={allGroups}
          setActiveGroup={setActiveGroup}
          setShowCreateNewGroupModal={setShowCreateNewGroupModal}
          activeGroup={activeGroup}
          allUsersCount={flattenAllOrgUsers(allGroups).length}
        />
        <GroupRoute
          allGroups={allGroups}
          renderGroup={renderGroup}
          organization={organization}
          setShowCreateNewGroupModal={setShowCreateNewGroupModal}
          deleteGroup={deleteGroupLocal}
          orgUser={orgUser}
          userToHighlight={userToHighlight}
          setUserToHighlight={setUserToHighlight}
          setConfirmTitle={setConfirmTitle}
          setConfirmMessage={setConfirmMessage}
          setSelectedUser={setSelectedUser}
          setShowConfirmModal={setShowConfirmModal}
        />
      </>
    );
  };

  const renderUsers = (users: Array<OrgUser>, groupName: string) => {
    return (
      <UserTable
        users={users}
        userToHighlight={userToHighlight}
        setUserToHighlight={setUserToHighlight}
        groupName={groupName}
        allGroups={allGroups}
        orgUser={orgUser}
        setConfirmTitle={setConfirmTitle}
        setConfirmMessage={setConfirmMessage}
        setSelectedUser={setSelectedUser}
        setShowConfirmModal={setShowConfirmModal}
        organization={organization}
        gridHeight="100%"
      />
    );
  };

  return (
    <>
      {fetchError ? (
        <ErrorComponent error={APIErrorCodes.FETCH_ERROR} />
      ) : (
        <>
          {!loading && allGroups && organization ? (
            <>{renderRoutes()}</>
          ) : (
            <LoadingSpinner />
          )}
        </>
      )}

      <LoadingData show={loadingAPIData} />
      <ConfirmModal
        show={showConfirmModal}
        confirm={removeUser}
        onHide={() => {
          setSelectedUser(undefined);
          setShowConfirmModal(false);
        }}
        confirmMessage={confirmMessage}
        confirmTitle={confirmTitle}
      />
      <EditGroupNameModal
        show={showEditGroupNameModal}
        onHide={() => setShowEditGroupNameModal(false)}
        organization={organization}
        group={changingGroupUserGroup}
        navigate={navigateToEditedGroup}
      />
      <InfoModal
        show={showInfoModal}
        onHide={() => setShowInfoModal(false)}
        infoMessage={infoMessage}
        infoTitle={infoTitle}
      />
      {organization && (
        <>
          <CreateGroupModal
            show={showCreateNewGroupModal}
            onHide={() => setShowCreateNewGroupModal(false)}
            orgCode={organization.org_code}
            orgAzureId={organization.id}
          />
        </>
      )}
    </>
  );
};

const RedirectingLoading = () => {
  const { t } = useTranslation();

  return <Loading loadingText={t(StringKeys.LOADING)} />;
};

export default withAuthenticationRequired(GroupManagement, {
  onRedirecting: RedirectingLoading,
});
